import { useEffect, useState } from 'react';
import { aTextoFamilias, compararFamilias, leerFamilias } from './delegationFamilies';
import { useGuardarConfiguracion } from './useGuardarConfiguracion';
import { ConfigCard, AvisoDeGuardado } from './ConfigCard';

/**
 * **«A nombre de»: familias excluidas** (Función 1, acuerdo con el PO del 2026-10-22).
 *
 * Decide quién puede aparecer como emisor en la caja «En representación de otro» y en la carga masiva:
 * personal operativo de la misma unidad, sin las familias de esta lista. El interruptor arranca apagado y,
 * mientras lo esté, la lista no tiene efecto. Mismo flujo que los centinelas (revisar el cambio y confirmar)
 * porque encender la regla o quitar una familia cambia a quién ve la gente en la caja.
 */
export function DelegationCard() {
  const { t, data, isLoading, isError, guardando, guardar, aviso, mensajeDeError } = useGuardarConfiguracion();

  const [texto, setTexto] = useState<string | null>(null);
  const [encendida, setEncendida] = useState<boolean | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  // Se resincroniza desde el servidor SÓLO mientras nadie lo esté editando (ver SentinelsCard).
  const guardada = data?.delegation;
  useEffect(() => {
    if (guardada && texto === null) setTexto(aTextoFamilias(guardada.excludedJobFunctions));
  }, [guardada, texto]);

  if (isLoading) return <p className="jobs-card-hint" role="status">{t('common.loading')}</p>;
  if (isError || !data) return <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.loadError')}</p>;
  // Un backend anterior a la Función 1 no devuelve la regla: no hay nada que editar todavía.
  if (!guardada) return null;

  const leidas = leerFamilias(texto ?? '');
  const cambio = compararFamilias(guardada.excludedJobFunctions, leidas.familias);
  const encendidaAhora = encendida ?? guardada.restrictionEnabled;
  const cambiaInterruptor = encendidaAhora !== guardada.restrictionEnabled;
  const hayCambios = cambiaInterruptor || cambio.anadidas.length > 0 || cambio.quitadas.length > 0;
  const hayErrores = leidas.problemas.some(p => p.tipo === 'formato');

  const guardarRegla = () => {
    setConfirmando(false);
    guardar(
      { delegation: { restrictionEnabled: encendidaAhora, excludedJobFunctions: leidas.familias } },
      t('jobs.sync.delegationSaved'),
      () => { setTexto(null); setEncendida(null); },
    );
  };

  return (
    <ConfigCard
      icono="fa-user-check"
      titulo={t('jobs.sync.delegation')}
      descripcion={t('jobs.sync.delegationDesc')}
      pie={
        <>
          <AvisoDeGuardado aviso={aviso} error={mensajeDeError} />
          {data.updatedBy && (
            <p className="jobs-card-meta">{t('jobs.sync.updatedBy')} {data.updatedBy}</p>
          )}
        </>
      }
    >
      <fieldset className="jobs-fieldset" disabled={guardando}>
        <label className="jobs-choice">
          <input
            type="checkbox"
            checked={encendidaAhora}
            onChange={e => { setEncendida(e.target.checked); setConfirmando(false); }}
          />
          <span>
            <strong>{t('jobs.sync.delegationEnabled')}</strong>
            <small>{t('jobs.sync.delegationEnabledHint')}</small>
          </span>
        </label>

        <label className="jobs-field-label" htmlFor="delegationFamilies">
          {t('jobs.sync.delegationHint')}
        </label>
        <textarea
          id="delegationFamilies"
          className="jobs-textarea"
          value={texto ?? ''}
          onChange={e => { setTexto(e.target.value); setConfirmando(false); }}
          rows={10}
          spellCheck={false}
          autoCapitalize="none"
        />

        <p className="jobs-card-meta">{t('jobs.sync.delegationCount', { n: leidas.familias.length })}</p>

        {leidas.problemas.length > 0 && (
          <ul className="jobs-problem-list jobs-alert jobs-alert--bad" role="alert">
            {leidas.problemas.map((problema, i) => (
              <li key={`${problema.tipo}-${problema.valor}-${i}`}>
                <code>{problema.valor}</code>{' — '}
                {problema.tipo === 'formato' ? t('jobs.sync.delegationBadFormat') : t('jobs.sync.domainDuplicate')}
              </li>
            ))}
          </ul>
        )}

        {/* No es un error: la lista vacía es válida, pero conviene saber qué queda filtrando. */}
        {leidas.familias.length === 0 && (
          <p className="jobs-alert jobs-alert--info" role="status">{t('jobs.sync.delegationEmpty')}</p>
        )}

        {hayCambios && (
          <div className="jobs-diff">
            {cambiaInterruptor && (
              <p>
                <strong>{encendidaAhora ? t('jobs.sync.delegationTurnOn') : t('jobs.sync.delegationTurnOff')}</strong>
              </p>
            )}
            {cambio.quitadas.length > 0 && (
              <p>
                <strong>{t('jobs.sync.domainsRemoved')}:</strong>{' '}
                <code>{cambio.quitadas.join(', ')}</code>
                <small>{t('jobs.sync.delegationRemovedWarning')}</small>
              </p>
            )}
            {cambio.anadidas.length > 0 && (
              <p>
                <strong>{t('jobs.sync.domainsAdded')}:</strong>{' '}
                <code>{cambio.anadidas.join(', ')}</code>
                <small>{t('jobs.sync.delegationAddedWarning')}</small>
              </p>
            )}
          </div>
        )}

        <div className="jobs-card-actions">
          {!confirmando ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!hayCambios || hayErrores || guardando}
              onClick={() => setConfirmando(true)}
            >
              {t('jobs.sync.domainsReview')}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn--primary btn--sm" onClick={guardarRegla} disabled={guardando}>
                {t('jobs.sync.domainsConfirm')}
              </button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => setConfirmando(false)} disabled={guardando}>
                {t('jobs.sync.domainsCancel')}
              </button>
            </>
          )}
        </div>
      </fieldset>
    </ConfigCard>
  );
}
